import { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import { NoticeModal } from 'components/ui/notice-modal';
import {
  CatalogRestockItem,
  getCatalogRestock,
  markCatalogRestockSeen,
} from 'services/experienceCataolog.service';
import { stripCatalogPrefix } from 'utils/experience-catalog';
import { DashboardModalSignals, dashboardModalsSettled } from '../modal-queue';

/*
 * Modal "item de volta ao estoque" do Catálogo de Experiências.
 *
 * É a ÚLTIMA modal automática do Dashboard (R10): busca os dados cedo, mas só abre
 * depois que todas as automáticas do registry (../modal-queue.ts) apareceram ou
 * decidiram não aparecer, e depois de a quietude ficar estável por DEBOUNCE_MS —
 * a janela absorve efeitos que abrem modal logo após o mount.
 *
 * Regras de fechamento:
 * - ✕ ou "Ver no catálogo" marcam o visto (marca-d'água = max(time) dos itens
 *   exibidos) — os itens visualizados não voltam (R5); uma liberação POSTERIOR
 *   tem time maior e volta a aparecer (D-1).
 * - Se alguma modal automática abrir por cima (sinais regridem), esta se fecha
 *   SOZINHA e SEM marcar visto — nunca disputa a tela; volta na próxima visita.
 * - Uma tentativa por carga: abriu (ou foi preemptada), não reabre no meio do uso.
 */

/** Quietude exigida antes de abrir. Exportado para os testes controlarem o relógio. */
export const RESTOCK_MODAL_DEBOUNCE_MS = 1500;

const CATALOG_ROUTE = '/catalogo-de-experiencias-list';

/** A marca-d'água que o fechamento grava: o evento mais novo exibido. */
export function maxSeenTime(items: CatalogRestockItem[]): number {
  return items.reduce((max, item) => (item.time > max ? item.time : max), 0);
}

export interface RestockMessage {
  prefix: string;
  /** Nomes sem o prefixo de catalogação, na ordem dos itens — renderizados em destaque. */
  names: string[];
  suffix: string;
}

/**
 * A frase única da modal (R4), com concordância:
 * - 1 item:  "O item X voltou a ficar disponível em nosso Catálogo de Experiências. Aproveite."
 * - 2 itens: "Os itens X e Y voltaram a ficar disponíveis em nosso Catálogo de Experiências. Aproveite."
 * - 3+ itens: "Os itens X, Y e Z voltaram…" (vírgulas, com "e" antes do último — norma pt-BR).
 * Os nomes ficam separados para o render poder destacá-los (negrito).
 */
export function buildRestockMessage(items: CatalogRestockItem[]): RestockMessage {
  const names = items.map(item => stripCatalogPrefix(item.nome));
  const singular = names.length === 1;

  return {
    prefix: singular ? 'O item ' : 'Os itens ',
    names,
    suffix: singular
      ? ' voltou a ficar disponível em nosso Catálogo de Experiências. Aproveite.'
      : ' voltaram a ficar disponíveis em nosso Catálogo de Experiências. Aproveite.',
  };
}

/** O que vai ANTES do nome de índice `index` na enumeração: nada, ", " ou " e " antes do último. */
export function nameSeparator(index: number, total: number): string {
  if (index === 0) {
    return '';
  }

  return index === total - 1 ? ' e ' : ', ';
}

export interface CatalogRestockModalProps {
  /** `canAccessExperienceCatalog(userInfo)` — decide inclusive se a API é chamada. */
  enabled: boolean;
  /** Chave do jogador (`userInfo.chave`) — destino da marca-d'água. */
  playerId?: string;
  signals: DashboardModalSignals;
}

export function useCatalogRestockModal({ enabled, playerId, signals }: CatalogRestockModalProps) {
  const [items, setItems] = useState<CatalogRestockItem[] | null>(null);
  const [isOpen, setIsOpen] = useState(false);
  const attemptedRef = useRef(false);

  const settled = dashboardModalsSettled(signals);

  // Busca cedo (uma vez, e só para elegíveis — o gate corta a chamada).
  useEffect(() => {
    if (!enabled || items !== null) {
      return;
    }

    let alive = true;
    getCatalogRestock().then(result => {
      if (alive) {
        setItems(result);
      }
    });

    return () => {
      alive = false;
    };
  }, [enabled, items]);

  // Abre tarde: lista não-vazia + quietude ESTÁVEL por DEBOUNCE_MS. Qualquer sinal
  // regredindo dentro da janela cancela o timer — e ele só volta quando a quietude
  // voltar, o que é exatamente o "última da fila".
  useEffect(() => {
    if (!enabled || isOpen || attemptedRef.current || !settled || !items || items.length === 0) {
      return;
    }

    const timer = setTimeout(() => {
      attemptedRef.current = true;
      setIsOpen(true);
    }, RESTOCK_MODAL_DEBOUNCE_MS);

    return () => clearTimeout(timer);
  }, [enabled, isOpen, settled, items]);

  // Preempção educada: outra automática abriu com esta na tela → fecha SEM visto.
  useEffect(() => {
    if (isOpen && !settled) {
      setIsOpen(false);
    }
  }, [isOpen, settled]);

  // Fechamento explícito (✕ ou botão): marca o visto e não volta nesta carga.
  const dismiss = useCallback(() => {
    if (playerId && items && items.length > 0) {
      markCatalogRestockSeen(playerId, maxSeenTime(items));
    }
    setIsOpen(false);
  }, [playerId, items]);

  return { isOpen, items: items ?? [], dismiss };
}

export default function CatalogRestockModal(props: CatalogRestockModalProps) {
  const navigate = useNavigate();
  const { isOpen, items, dismiss } = useCatalogRestockModal(props);

  if (!isOpen || items.length === 0) {
    return null;
  }

  const message = buildRestockMessage(items);

  const goToCatalog = () => {
    // O visto é gravado ANTES de navegar — sair do Dashboard fecha a modal por
    // desmontagem, e sem a marca ela voltaria na próxima visita (R5).
    dismiss();
    navigate(CATALOG_ROUTE);
  };

  return (
    <NoticeModal
      isOpen={isOpen}
      title="Catálogo de Experiências"
      actionLabel="Ver no catálogo"
      onAction={goToCatalog}
      onClose={dismiss}
      closeLabel="Fechar aviso do Catálogo de Experiências"
    >
      <p className="m-0">
        {message.prefix}
        {message.names.map((name, index) => (
          <span key={items[index].item}>
            {nameSeparator(index, message.names.length)}
            <strong className="font-bold text-brand-blue-dark">{name}</strong>
          </span>
        ))}
        {message.suffix}
      </p>
    </NoticeModal>
  );
}
